import { Set as ISet, List } from '@gitbook/util-immutable';
import { assertNever } from '@gitbook/util-ts';

import { Block } from './block';
import { Data } from './data';
import { Document } from './document';
import { Fragment } from './fragment';
import { Inline } from './inline';
import { Leaf } from './leaf';
import { Mark } from './mark';
import { type AnyOperation, Operation, type OperationType } from './operation';
import { Text } from './text';
import { TextRange } from './text-range';
import { Value, type ValueCreateProps } from './value';

export type DataJSON = object;

/** JSON representation of a mark */
export interface MarkJSON {
    object: 'mark';
    data: DataJSON;
    type: string;
}

/** JSON representation of a leaf */
export interface LeafJSON {
    object: 'leaf';
    text: string;
    marks: MarkJSON[];
}

/** JSON representation of a Text. */
export interface TextJSON {
    key?: string;
    object: 'text';
    leaves: LeafJSON[];
}

/** JSON representation of an inline node. */
export interface InlineJSON {
    object: 'inline';
    key?: string;
    type: string;
    isVoid: boolean;
    data: DataJSON;
    nodes: Array<InlineJSON | TextJSON>;
    fragments?: FragmentJSON[];
}

/** JSON representation of a fragment */
export interface FragmentJSON {
    object: 'fragment';
    key?: string;
    fragment?: string;
    type?: string;
    nodes: Array<BlockJSON | InlineJSON>;
}

/** JSON representation of a block node */
export interface BlockJSON {
    object: 'block';
    key?: string;
    data: DataJSON;
    isVoid: boolean;
    nodes: Array<BlockJSON | InlineJSON>;
    fragments?: FragmentJSON[];
    type: string;
}

/** JSON representation of a document node */
export interface DocumentJSON {
    key?: string;
    object: 'document';
    data: DataJSON;
    nodes: BlockJSON[];
}

/** JSON representation of a range */
export interface RangeJSON {
    object: 'range';
    anchorKey: string | null;
    anchorOffset: number;
    focusKey: string | null;
    focusOffset: number;
    isBackward: boolean | null;
    isFocused: boolean;
    isAtomic: boolean;
    marks: MarkJSON[] | null;
    data: DataJSON;
}

/** JSON representation of a value node */
export interface ValueJSON {
    object: 'value';
    document: DocumentJSON;
    data?: DataJSON;
    selection?: Omit<RangeJSON, 'anchorKey' | 'focusKey'> & {
        anchorKey?: string | null;
        focusKey?: string | null;
        anchorPath?: number[];
        focusPath?: number[];
    };
}

/** JSON representation of an operation */
export interface OperationJSON {
    object: 'operation';
    type: OperationType;
    [key: string]: any;
}

export type NodeJSON =
    | MarkJSON
    | LeafJSON
    | TextJSON
    | InlineJSON
    | BlockJSON
    | FragmentJSON
    | DocumentJSON
    | ValueJSON
    | RangeJSON
    | OperationJSON;
export type NodeType =
    | Mark
    | Leaf
    | Text
    | Inline
    | Block
    | Fragment
    | Document
    | Value
    | TextRange
    | Operation;
export type KeyedNodeType = Text | Inline | Block | Fragment | Document;

/**
 * Create a `Node` from a JSON `value`
 */
export function deserialize(value: MarkJSON): Mark;
export function deserialize(value: LeafJSON): Leaf;
export function deserialize(value: TextJSON): Text;
export function deserialize(value: InlineJSON): Inline;
export function deserialize(value: BlockJSON): Block;
export function deserialize(value: FragmentJSON): Fragment;
export function deserialize(value: DocumentJSON): Document;
export function deserialize(value: ValueJSON): Value;
export function deserialize(value: OperationJSON): Operation;
export function deserialize(value: RangeJSON): TextRange;
export function deserialize(value: NodeJSON): NodeType {
    // Handle "kind" to easily deserialzie document from gitbook-new
    switch (value.object || (value as { kind?: NodeJSON['object'] }).kind) {
        case 'mark': {
            const mark = value as MarkJSON;
            return Mark.create({
                type: mark.type,
                data: mark.data,
            });
        }
        case 'leaf': {
            const leaf = value as LeafJSON;
            return Leaf.create({
                text: leaf.text,
                marks: (leaf.marks ?? []).map((mark) => deserialize(mark)),
            });
        }
        case 'text': {
            const text = value as TextJSON;
            return Text.create({
                ...text,
                leaves: text.leaves
                    ? text.leaves.map((leaf) => deserialize(leaf))
                    : // Legacy support for https://github.com/GitbookIO/gitbook-new/blob/master/modules/edition/serialize.js#L56
                      (text as { ranges?: LeafJSON[] }).ranges!.map((leaf) =>
                          deserialize({ ...leaf, kind: 'leaf' } as LeafJSON)
                      ),
            });
        }
        case 'inline': {
            const inline = value as InlineJSON;
            return Inline.create({
                ...inline,
                fragments: inline.fragments
                    ? inline.fragments.map((fragment) => deserialize(fragment))
                    : [],
                nodes: (inline.nodes ?? []).map((node) => deserialize(node as InlineJSON)),
            });
        }
        case 'fragment': {
            const fragment = value as FragmentJSON;
            return Fragment.create({
                ...fragment,
                nodes: fragment.nodes.map((node) => deserialize(node as BlockJSON)),
            });
        }
        case 'block': {
            const block = value as BlockJSON;
            return Block.create({
                ...block,
                fragments: block.fragments
                    ? block.fragments.map((fragment) => deserialize(fragment))
                    : [],
                nodes: (block.nodes ?? []).map((node) => deserialize(node as BlockJSON)),
            });
        }
        case 'document': {
            const documentJSON = value as DocumentJSON;
            return Document.create({
                ...documentJSON,
                nodes: documentJSON.nodes.map((node) => deserialize(node)),
            });
        }
        case 'range':
            return TextRange.create(value as RangeJSON);
        case 'value': {
            const valueJSON = value as ValueJSON;
            return Value.create({
                document: deserialize(valueJSON.document),
                data: Data.create(valueJSON.data || {}),
                selection: valueJSON.selection
                    ? deserialize(valueJSON.selection as RangeJSON)
                    : undefined,
            } as ValueCreateProps);
        }
        case 'operation':
            return deserializeOperation(value as OperationJSON);
        default: {
            assertNever(value as never);
        }
    }
}

interface SerializeOptions {
    /** Preserve keys on every nodes */
    preserveKeys?: boolean;

    /** Preserve data when serializing value */
    preserveDataValue?: boolean;

    /** Preserve selection when serializing value */
    preserveSelection?: boolean;

    /** Transform a node to add annotation */
    transform?: (node: NodeType, json: NodeJSON) => NodeJSON;
}

/**
 * Convert a `Node` to a JSON `value`.
 */
export function serialize(
    value: List<NodeType> | ISet<NodeType>,
    options?: SerializeOptions
): NodeJSON[];
export function serialize(value: Mark): MarkJSON;
export function serialize(value: Leaf): LeafJSON;
export function serialize(value: Text): TextJSON;
export function serialize(value: Inline): InlineJSON;
export function serialize(value: Fragment, options?: SerializeOptions): FragmentJSON;
export function serialize(value: Block, options?: SerializeOptions): BlockJSON;
export function serialize(value: Document, options?: SerializeOptions): DocumentJSON;
export function serialize(value: Operation, options?: SerializeOptions): OperationJSON;
export function serialize(value: TextRange): RangeJSON;
export function serialize(value: Value, options?: SerializeOptions): ValueJSON;
export function serialize(
    value: NodeType | List<NodeType> | ISet<NodeType>,
    options?: SerializeOptions
): NodeJSON | NodeJSON[] {
    const { transform = defaultTransform } = options || {};

    if (List.isList(value) || ISet.isSet(value)) {
        return value.toArray().map((el) => serializeNode(el, options));
    }

    return transform(
        value,
        (() => {
            switch (value.object) {
                case 'mark':
                    return {
                        object: value.object,
                        type: value.type,
                        data: value.data.toObject(),
                    };
                case 'leaf':
                    return {
                        object: value.object,
                        text: value.text,
                        marks: value.marks
                            .toArray()
                            .sort((a, b) => a.type.localeCompare(b.type))
                            .map((m) => serialize(m)),
                    };
                case 'text':
                    return {
                        object: value.object,
                        leaves: value
                            .getLeaves()
                            .toArray()
                            .map((leaf) => serialize(leaf)),
                        ...(options?.preserveKeys && { key: value.key }),
                    };
                case 'inline':
                    return {
                        object: value.object,
                        type: value.type,
                        isVoid: value.isVoid,
                        data: value.data.toObject(),
                        nodes: value.nodes.toArray().map((child) => serializeNode(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                        ...(value.fragments.size
                            ? {
                                  fragments: value.fragments
                                      .toArray()
                                      .map(([_key, fragment]) => serialize(fragment, options)),
                              }
                            : {}),
                    };
                case 'fragment':
                    return {
                        object: value.object,
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                        ...(value.fragment && { fragment: value.fragment }),
                        ...(value.type && { type: value.type }),
                    };
                case 'block':
                    return {
                        object: value.object,
                        type: value.type,
                        isVoid: value.isVoid,
                        data: value.data.toObject(),
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                        ...(value.fragments.size
                            ? {
                                  fragments: value.fragments
                                      .toArray()
                                      .map(([_key, fragment]) => serialize(fragment, options)),
                              }
                            : {}),
                    };
                case 'document':
                    return {
                        object: value.object,
                        data: value.data.toObject(),
                        nodes: value.nodes.toArray().map((child) => serialize(child, options)),
                        ...(options?.preserveKeys && { key: value.key }),
                    };
                case 'range':
                    return {
                        object: value.object,
                        anchorKey: value.anchorKey,
                        anchorOffset: value.anchorOffset,
                        focusKey: value.focusKey,
                        focusOffset: value.focusOffset,
                        isBackward: value.isBackward,
                        isFocused: value.isFocused,
                        isAtomic: value.isAtomic,
                        marks:
                            value.marks == null
                                ? null
                                : value.marks.toArray().map((m) => serialize(m)),
                        data: value.data.toObject(),
                    };
                case 'value':
                    return serializeValue(value, options);
                case 'operation':
                    return serializeOperation(value, options);
                default: {
                    assertNever(value);
                }
            }
        })() as NodeJSON
    );
}

/**
 * The public `serialize` overloads omit the `options` argument on single-node
 * signatures, even though the implementation forwards `options` to `transform`
 * for every node. This alias lets internal callers serialize a node of unknown
 * concrete type while still passing options; runtime dispatch is unchanged since
 * it keys off `value.object`.
 */
const serializeNode = serialize as (value: NodeType, options?: SerializeOptions) => NodeJSON;

function serializeValue(value: Value, options?: SerializeOptions): ValueJSON {
    const object: ValueJSON = {
        object: value.object,
        document: serialize(value.document, options),
        ...(options?.preserveDataValue && {
            data: (value as unknown as { data: Data }).data.toObject(),
        }),
    };

    // When serializing valuew ithout keys, we store paths for the selection.
    if (options?.preserveSelection && !options?.preserveKeys) {
        const { document, selection } = value;

        object.selection = serialize(value.selection as TextRange);

        object.selection!.anchorPath = (
            selection.isSet ? document.getPath((selection as TextRange).anchorKey!) : null
        ) as number[] | undefined;

        object.selection!.focusPath = (
            selection.isSet ? document.getPath((selection as TextRange).focusKey!) : null
        ) as number[] | undefined;

        delete object.selection!.anchorKey;
        delete object.selection!.focusKey;
    }

    return object;
}

function serializeOperation(operation: Operation, options?: SerializeOptions): OperationJSON {
    switch (operation.type) {
        // Texts
        case 'insert_text':
        case 'remove_text':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                text: operation.text,
                marks: operation.marks.toArray().map((mark) => serializeNode(mark, options)),
            };
        // Marks
        case 'add_mark':
        case 'remove_mark':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                length: operation.length,
                mark: serializeNode(operation.mark, options),
            };
        // Nodes
        case 'insert_node':
        case 'remove_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                node: serializeNode(operation.node, options),
            };
        case 'move_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                newPath: operation.newPath,
            };
        case 'merge_node':
        case 'split_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                position: operation.position,
                properties: operation.properties,
                target: operation.target,
            };
        case 'set_node':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                node: serializeNode(operation.node, options),
                properties: operation.properties,
            };
        // Fragments
        case 'insert_fragment':
        case 'set_fragment':
        case 'remove_fragment':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                ...(operation.fragmentPath ? { fragmentPath: operation.fragmentPath } : {}),
                fragmentIndex: operation.fragmentIndex,
                node: serializeNode(operation.node, options),
            };
        // Data
        case 'set_data':
        case 'remove_data':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
            };
        case 'insert_array_data':
        case 'remove_array_data':
            return {
                object: 'operation',
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
                dataIndex: operation.dataIndex,
            };
        // Selection
        case 'set_selection':
            return {
                object: 'operation',
                type: operation.type,
                selection: serialize(operation.selection as TextRange),
                properties: operation.properties,
            };
        // Document
        case 'set_document':
            return {
                object: 'operation',
                type: operation.type,
                data: serialize(operation.data),
            };
        default:
            assertNever(operation as never);
    }
}

function deserializeOperation(operation: OperationJSON): Operation {
    switch (operation.type) {
        // Texts
        case 'insert_text':
        case 'remove_text':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                text: operation.text,
                marks: Mark.createSet(operation.marks.map((mark) => deserialize(mark))),
            });
        // Marks
        case 'add_mark':
        case 'remove_mark':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                offset: operation.offset,
                length: operation.length,
                mark: deserialize(operation.mark),
            });
        // Nodes
        case 'insert_node':
        case 'remove_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                node: deserialize(operation.node as BlockJSON),
            });
        case 'move_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                newPath: operation.newPath,
            });
        case 'merge_node':
        case 'split_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                position: operation.position,
                properties: operation.properties,
                target: operation.target,
            });
        case 'set_node':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                node: deserialize(operation.node as BlockJSON),
                properties: operation.properties,
            });
        // Fragments
        case 'insert_fragment':
        case 'set_fragment':
        case 'remove_fragment':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                ...(operation.fragmentPath ? { fragmentPath: operation.fragmentPath } : {}),
                fragmentIndex: operation.fragmentIndex,
                node: deserialize(operation.node as FragmentJSON),
            } as AnyOperation);
        // Data
        case 'set_data':
        case 'remove_data':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
            });
        case 'insert_array_data':
        case 'remove_array_data':
            return Operation.create({
                type: operation.type,
                path: operation.path,
                dataPath: operation.dataPath,
                data: operation.data,
                dataIndex: operation.dataIndex,
            });
        // Selection
        case 'set_selection':
            return Operation.create({
                type: operation.type,
                selection: deserialize(operation.selection as RangeJSON),
                properties: operation.properties,
            });
        // Document
        case 'set_document':
            return Operation.create({
                type: operation.type,
                data: deserialize(operation.data as DocumentJSON),
            });
        default:
            assertNever(operation as never);
    }
}

/**
 * Serialize an array of operations.
 */
export function serializeOperations(operations: Operation[]): string {
    const serialized: OperationJSON[] = operations.map((operation) =>
        serialize(operation, { preserveKeys: true })
    );
    const stringified = JSON.stringify(serialized, null, 2);
    return stringified;
}

/**
 * Deserialize an array of operations.
 */
export function deserializeOperations(operations: string): Operation[] {
    const parsed: OperationJSON[] = JSON.parse(operations);
    const deserialized: Operation[] = parsed.map((operation) => deserialize(operation));
    return deserialized;
}

function defaultTransform(_node: NodeType, json: NodeJSON): NodeJSON {
    return json;
}
